import { atom } from 'recoil';

/**
 * "이 주문 내용 그대로 채우기" 요청 (메모리 전용 — 보존하지 않는다).
 *
 * 대리주문 중 옆 패널의 상가 주문 내역에서 감사글/배송지를 눌러 담으면,
 * 이미 마운트된 주문서(감사 메시지 섹션·배송지 입력)가 이 요청을 받아 입력값을 바꾼다.
 * 자동 복원(보존값)과 달리 **직원이 보고 누른** 값이므로 명의가 달라도 적용하지만,
 * 엉뚱한 화면에 들어가지 않도록 bugoId를 함께 확인한다.
 */
export interface GiftInputApplyRequest {
  /** 이 요청을 적용할 부고 — 화면의 bugoId와 같을 때만 반영 */
  bugoId: string;
  /** 같은 내용을 다시 눌러도 반영되도록 요청마다 새 값 */
  requestedAt: number;
  thanksPrint?: {
    message: string;
    /** 발신문구 원본 ("홍길동 배상") — 이름/표현으로 분해해 채운다 */
    senderName?: string;
    templateId?: string;
  };
  deliveryAddress?: {
    address: string;
    postalCode: string;
    addressDetail?: string;
  };
  /**
   * 주문서를 지금 상주 기준으로 정리 — 감사글·보내는 이는 상주 기본값으로,
   * 배송지는 입력·보존값 모두 비운다. (다른 가족 내용을 담아본 뒤 되돌리는 용도)
   */
  resetToMemberDefault?: boolean;
}

export const giftInputApplyRequestAtom = atom<GiftInputApplyRequest | null>({
  key: 'giftInputApplyRequestAtom',
  default: null,
});

/**
 * 이미 반영한 요청인지 — **컴포넌트 인스턴스가 아니라 모듈 단위로** 기억한다.
 *
 * useRef로 1회 가드를 걸면 페이지를 오갈 때마다 새 인스턴스의 ref가 null이라
 * 같은 요청이 다시 발동해, 그 사이 직원이 입력한 값을 또 덮어쓴다.
 * ("주문서 정리" 후 상세 → 체크아웃 복귀 시 방금 넣은 배송지가 다시 비워지던 문제)
 *
 * 요청 자체를 소비 직후 null로 비우지 않는 이유: 소비자가 여러 곳(감사글·배송지·
 * 상품옵션)이고 화면마다 마운트되는 조합이 달라, 먼저 본 하나가 비우면 나머지가
 * 요청을 놓친다. 그래서 "누가 어떤 요청까지 봤는지"만 따로 기록한다.
 * 메모리 전용이라 새로고침하면 요청·기록이 함께 사라진다.
 */
const consumedRequests = new Map<string, number>();

/** 이 소비자가 이 요청을 처음 보는 경우에만 true (동시에 소비 처리) */
export const consumeGiftInputApplyRequest = (
  consumer: string,
  requestedAt: number,
): boolean => {
  if (consumedRequests.get(consumer) === requestedAt) return false;
  consumedRequests.set(consumer, requestedAt);
  return true;
};
